import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'

export interface MatrixUrlPreview {
  url: string
  title: string | null
  description: string | null
  image: string | null
  siteName: string | null
}

const cache = new Map<string, MatrixUrlPreview | null>()
const failedAt = new Map<string, number>()
const inFlight = new Map<string, Promise<MatrixUrlPreview | null>>()

const FAILURE_RETRY_TTL_MS = 5 * 60_000

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

function isSameOriginUrl(url: string): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return new URL(url, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

export async function fetchMatrixUrlPreview(url: string): Promise<MatrixUrlPreview | null> {
  const trimmed = url.trim()
  if (!trimmed || isSameOriginUrl(trimmed)) {
    return null
  }

  if (cache.has(trimmed)) {
    return cache.get(trimmed) ?? null
  }

  const lastFailure = failedAt.get(trimmed)
  if (lastFailure && Date.now() - lastFailure < FAILURE_RETRY_TTL_MS) {
    return null
  }

  const pending = inFlight.get(trimmed)
  if (pending) {
    return pending
  }

  const promise = (async () => {
    try {
      const client = await startMatrixClient()
      const raw = await client.getUrlPreview(trimmed, Date.now())
      const preview: MatrixUrlPreview = {
        url: String(raw['og:url'] || trimmed),
        title: typeof raw['og:title'] === 'string' ? raw['og:title'] : null,
        description: typeof raw['og:description'] === 'string' ? raw['og:description'] : null,
        image: typeof raw['og:image'] === 'string' ? raw['og:image'] : null,
        siteName: hostOf(String(raw['og:url'] || trimmed)),
      }

      failedAt.delete(trimmed)

      if (!preview.title && !preview.description && !preview.image) {
        cache.set(trimmed, null)
        return null
      }

      cache.set(trimmed, preview)
      return preview
    } catch {
      failedAt.set(trimmed, Date.now())
      return null
    } finally {
      inFlight.delete(trimmed)
    }
  })()

  inFlight.set(trimmed, promise)
  return promise
}
